import React from 'react';

interface VestaboardPreviewProps {
  characters: number[][];
}

const COLOR_MAP: Record<number, string> = {
  0: '#000000',    // Blank/Black
  63: '#FF0000',   // Red
  64: '#FFA500',   // Orange
  65: '#FFFF00',   // Yellow
  66: '#00FF00',   // Green
  67: '#0000FF',   // Blue
  68: '#8A2BE2',   // Violet
  69: '#FFFFFF',   // White
  70: '#000000',   // Black
  71: '#808080',   // Gray (filled)
};

const CHAR_MAP: Record<number, string> = {
  0: ' ',
  1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E', 6: 'F', 7: 'G', 8: 'H', 9: 'I', 10: 'J',
  11: 'K', 12: 'L', 13: 'M', 14: 'N', 15: 'O', 16: 'P', 17: 'Q', 18: 'R', 19: 'S',
  20: 'T', 21: 'U', 22: 'V', 23: 'W', 24: 'X', 25: 'Y', 26: 'Z',
  27: '1', 28: '2', 29: '3', 30: '4', 31: '5', 32: '6', 33: '7', 34: '8', 35: '9', 36: '0',
  37: '!', 38: '@', 39: '#', 40: '$', 41: '(', 42: ')', 44: '-', 46: '+', 47: '&',
  48: '=', 49: ';', 50: ':', 52: "'", 53: '"', 54: '%', 55: ',', 56: '.', 59: '/', 60: '?', 62: '°'
};

function getDisplayChar(code: number): string {
  // Color codes should show as a colored block
  if (code >= 63 && code <= 71) {
    return '█';
  }
  return CHAR_MAP[code] || ' ';
}

function getCharColor(code: number): string {
  if (code >= 63 && code <= 71) {
    return COLOR_MAP[code] || '#000000';
  }
  return '#FFFFFF'; // Default text color
}

function getBackgroundColor(code: number): string {
  if (code >= 63 && code <= 71) {
    return COLOR_MAP[code] || '#000000';
  }
  return '#000000'; // Default background
}

export function VestaboardPreview({ characters }: VestaboardPreviewProps) {
  if (!characters || characters.length === 0) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Vestaboard Preview</h3>
        <div style={styles.boardContainer}>
          <div style={styles.grid}>
            {Array.from({ length: 6 }, (_, row) =>
              Array.from({ length: 22 }, (_, col) => (
                <div key={`${row}-${col}`} style={styles.cell}>
                  <span style={styles.char}> </span>
                </div>
              ))
            )}
          </div>
          <div style={styles.branding}>VESTABOARD</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Vestaboard Preview</h3>
      <div style={styles.boardContainer}>
        <div style={styles.grid}>
          {characters.map((row, rowIndex) =>
            row.map((code, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                style={{
                  ...styles.cell,
                  backgroundColor: getBackgroundColor(code),
                }}
              >
                <span
                  style={{
                    ...styles.char,
                    color: getCharColor(code),
                  }}
                >
                  {getDisplayChar(code)}
                </span>
              </div>
            ))
          )}
        </div>
        <div style={styles.branding}>VESTABOARD</div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    marginBottom: '20px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  title: {
    textAlign: 'center',
    marginBottom: '20px',
    color: '#333',
    fontSize: '20px',
    fontWeight: 'bold',
  },
  boardContainer: {
    maxWidth: '760px',
    margin: '0 auto',
    border: '12px solid #c0c0c0',
    borderRadius: '8px',
    backgroundColor: '#2a2a2a',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 2px 8px rgba(255,255,255,0.1)',
    position: 'relative',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(22, 1fr)',
    gridTemplateRows: 'repeat(6, 1fr)',
    gap: '2px',
    padding: '24px 24px 16px 24px',
  },
  branding: {
    textAlign: 'center',
    color: '#999',
    fontSize: '11px',
    fontWeight: '500',
    letterSpacing: '1.5px',
    padding: '0 0 16px 0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  cell: {
    width: '28px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: '2px',
    border: '1px solid #3a3a3a',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.05)',
  },
  char: {
    fontSize: '16px',
    fontFamily: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    lineHeight: '1',
    letterSpacing: '0',
  },
};